/**
 * OrderNotesPopover. The order notes affordance: a notes icon (with a has-notes
 * count badge) that opens the shared centered <NotesModal> (COCKPIT.md section
 * 2.10) as an overlay. Used on both the Orders grid row and the order detail
 * page so the affordance is identical in both places.
 *
 * The modal shows BOTH the order's notes (commerce_order_notes) and its audit
 * trail (the order audit feed), the centered look Steven prefers. The modal
 * opens in place and never navigates (the old per-tool order notes popup had a
 * bug where it navigated to a blank page; this supersedes it).
 *
 * Notes + audit load lazily on first open (grid rows do not fire a notes/audit
 * request per visible row on first paint). The detail page can pass `eager` to
 * load on mount. The name is kept for call-site compatibility; it now opens the
 * shared centered modal rather than an anchored popover.
 */

import { useState } from 'react';
import { NotesModal } from '@/components/NotesModal';
import { NotesIconButton } from '@/components/RowActionIcons';
import { formatDateTime } from '@/lib/dateUtils';
import {
  formatOrderNumber,
  useAddNote,
  useOrderAudit,
  useOrderNotes,
} from '@/lib/useOrders';

export function OrderNotesPopover({
  orderId,
  sourceOrderId,
  notesCount,
  canWrite,
  eager = false,
}: {
  orderId: string;
  sourceOrderId?: string | null;
  notesCount: number;
  canWrite: boolean;
  eager?: boolean;
}) {
  const [open, setOpen] = useState(false);
  // Load notes + audit once the modal has been opened (unless eager).
  const [activated, setActivated] = useState(eager);
  const notesQ = useOrderNotes(orderId, activated);
  const auditQ = useOrderAudit(orderId, activated);
  const addNote = useAddNote();

  const notes = (notesQ.data?.notes ?? []).map((n) => ({
    id: n.note_id,
    author: n.author_email,
    body: n.body,
    created_at: n.created_at,
  }));

  const audit = (auditQ.data?.events ?? []).map((e) => ({
    id: e.audit_id,
    actor: e.actor_email,
    action: e.action,
    outcome: e.outcome,
    detail: e.detail,
    occurred_at: e.occurred_at,
  }));

  // Prefer the loaded count once notes have loaded; otherwise the row count.
  const count = notesQ.data ? notes.length : notesCount;
  const title = sourceOrderId ? formatOrderNumber(sourceOrderId) : orderId.slice(0, 8);

  return (
    <>
      <NotesIconButton
        count={count}
        onOpen={() => {
          setActivated(true);
          setOpen(true);
        }}
      />
      {open && (
        <NotesModal
          title={title}
          eyebrow="Order"
          notes={notes}
          notesLoading={notesQ.isLoading}
          notesError={notesQ.error?.message ?? null}
          audit={audit}
          auditLoading={auditQ.isLoading}
          auditError={auditQ.error?.message ?? null}
          canWrite={canWrite}
          adding={addNote.isPending}
          addError={addNote.error?.message ?? null}
          onAddNote={(body) => addNote.mutate({ orderId, body })}
          formatTimestamp={formatDateTime}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
